import { useEffect, useState } from "react";
import { HierarchyManager } from "../../components/HierarchyManager";
import { FormFooter, FormButton } from "../../components/Form";
import { editionConfig, loadDirectoryOptions, type EditionWithHierarchy } from "./editionConfig";
import styles from "./Editions.module.css";

function EditionsFormHeader({
  modal,
  error,
}: {
  modal: { type: "create" | "edit"; item?: EditionWithHierarchy };
  error: Error | null;
}) {
  const isArchived = modal.type === "edit" && modal.item?.status === "archived";

  if (isArchived) {
    return (
      <div className={styles.archivedNotice} role="alert">
        This edition is archived and can no longer be edited. Create a new edition if needed.
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error} role="alert">
          <span>{error.message}</span>
        </div>
      </div>
    );
  }

  return null;
}

function EditionsPage() {
  const [config, setConfig] = useState(editionConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  async function loadOptions() {
    try {
      const directoryOptions = await loadDirectoryOptions();
      setConfig(prevConfig => {
        const directoryField = prevConfig.formFields.find(f => f.name === "directoryId");
        if (directoryField) {
          directoryField.options = directoryOptions;
        }
        return { ...prevConfig };
      });
    } catch (error) {
      setError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    setConfig(prevConfig => ({
      ...prevConfig,
      formHeader: (modal: { type: "create" | "edit"; item?: EditionWithHierarchy }) => (
        <EditionsFormHeader modal={modal} error={error} />
      ),
      formFooter: ({ modal, onClose, onDelete, submitting }) => {
        const isArchived = modal.type === "edit" && modal.item?.status === "archived";

        return (
          <FormFooter>
            <div>
              {!isArchived && modal.type === "edit" && onDelete && (
                <FormButton onClick={onDelete} variant="danger" disabled={submitting}>
                  Delete
                </FormButton>
              )}
            </div>
            <div>
              <div style={{ display: "flex", gap: "12px" }}>
                <FormButton onClick={onClose} disabled={submitting}>
                  {isArchived ? "Close" : "Cancel"}
                </FormButton>
                {!isArchived && (
                  <FormButton type="submit" variant="primary" disabled={submitting}>
                    {submitting ? "Saving..." : modal.type === "create" ? "Create" : "Save"}
                  </FormButton>
                )}
              </div>
            </div>
          </FormFooter>
        );
      },
    }));
  }, [error]);

  return (
    <div className={styles.container}>
      {isLoading ? <div>Loading...</div> : <HierarchyManager config={config} />}
    </div>
  );
}

export default EditionsPage;
export { EditionsFormHeader };
